import { createContext, useContext } from "react";

export const CATALOG_TYPES = ["carousel", "productList", "pdfViewer"];

export const CatalogContext = createContext({ currency: "₹", products: [] });
export const useCatalog = () => useContext(CatalogContext) || { currency: "₹", products: [] };

const pick = (o, keys) => {
  for (const k of keys) if (o[k] != null && o[k] !== "") return o[k];
  return undefined;
};

export const toList = v => {
  if (Array.isArray(v)) return v.filter(x => x != null && x !== "").map(x => String(x).trim()).filter(Boolean);
  if (typeof v === "string") return v.split(/[,\n]/).map(x => x.trim()).filter(Boolean);
  return [];
};

const getPath = (obj, path) => String(path || "").split(".").filter(Boolean).reduce((acc, k) => (acc == null ? acc : acc[k]), obj);

const toNumber = v => {
  if (v == null || v === "") return null;
  const n = Number(String(v).replace(/[^0-9.-]/g, ""));
  return Number.isFinite(n) ? n : null;
};

export function normalizeProduct(raw, idx = 0) {
  if (!raw || typeof raw !== "object") return null;
  const price = toNumber(pick(raw, ["price", "Price", "amount"]));
  const mrp = toNumber(pick(raw, ["mrp", "compareAtPrice", "originalPrice"]));
  const isFree = raw.isFree === true || raw.isFree === "true" || price === 0;
  const meta = Array.isArray(raw.meta)
    ? raw.meta.filter(m => m && (m.label || m.value)).map(m => ({ icon: m.icon || "", label: m.label || "", value: m.value == null ? "" : String(m.value) }))
    : [];
  return {
    ...raw,
    id: String(pick(raw, ["id", "_id", "sku", "productId"]) ?? `p-${idx}`),
    title: String(pick(raw, ["title", "name", "Title", "Name"]) ?? ""),
    image: pick(raw, ["image", "thumbnail", "cover", "imageUrl", "img", "Image"]) || "",
    images: toList(raw.images || raw.gallery),
    shortDesc: String(pick(raw, ["shortDesc", "summary", "shortDescription", "short_description"]) ?? ""),
    description: String(pick(raw, ["description", "desc", "longDescription", "Description"]) ?? ""),
    price,
    mrp,
    currency: raw.currency || "",
    isFree,
    tags: toList(raw.tags),
    meta,
    pdfUrl: pick(raw, ["pdfUrl", "pdf", "fileUrl", "documentUrl"]) || "",
    ctaLabel: raw.ctaLabel || "",
  };
}

export function getProducts(node, catalog, globalDataStore) {
  let rows = [];
  if (node.productSource === "dataSource" && node.productSourceId) {
    let d = globalDataStore?.[node.productSourceId];
    if (d && !Array.isArray(d) && node.productSourceKey) d = getPath(d, node.productSourceKey);
    rows = Array.isArray(d) ? d : [];
  } else {
    rows = Array.isArray(catalog?.products) ? catalog.products : [];
  }
  return rows.map(normalizeProduct).filter(Boolean);
}

export function activeFilterTags(node, vars) {
  return [...toList(node.productFilterTags), ...(node.productTagVar ? toList(vars?.[node.productTagVar]) : [])];
}

export function filterProducts(products, node, vars) {
  let list = products;
  const tags = activeFilterTags(node, vars).map(t => t.toLowerCase());
  if (tags.length) {
    list = list.filter(p => {
      const pt = p.tags.map(t => t.toLowerCase());
      return node.productTagMatch === "any" ? tags.some(t => pt.includes(t)) : tags.every(t => pt.includes(t));
    });
  }
  const q = node.productSearchVar ? String(vars?.[node.productSearchVar] ?? "").trim().toLowerCase() : "";
  if (q) {
    list = list.filter(p => [p.title, p.shortDesc, p.description, ...p.tags, ...p.meta.map(m => m.value)]
      .some(v => String(v || "").toLowerCase().includes(q)));
  }
  return list;
}

export function priceLabel(p, catalog, node) {
  if (p.isFree || p.price == null) return node?.productFreeLabel || "";
  const cur = p.currency || catalog?.currency || "₹";
  return `${cur}${p.price.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
}

export function mrpLabel(p, catalog) {
  if (p.isFree || p.mrp == null || p.price == null || p.mrp <= p.price) return "";
  const cur = p.currency || catalog?.currency || "₹";
  return `${cur}${p.mrp.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
}

export const selectVarName = node => node.productSelectVar || "selectedProduct";

export function findSelectedProduct(node, products, vars, fallbackFirst) {
  const id = node.productDisplay === "static" ? node.productId : vars?.[selectVarName(node)];
  const found = id != null && id !== "" ? products.find(p => p.id === String(id)) : null;
  return found || (fallbackFirst ? products[0] || null : null);
}

export function withSelect(node, actions) {
  return [{ id: "act-select-product", type: "setVar", varName: selectVarName(node), value: "$id" }, ...(actions || [])];
}

export function resolvePdfUrl(node, products, vars, fallbackFirst) {
  if (node.pdfSource === "variable") return node.pdfUrlVar ? String(vars?.[node.pdfUrlVar] ?? "") : "";
  if (node.pdfSource === "product") {
    const p = findSelectedProduct(node, products, vars, false) || (fallbackFirst ? products.find(x => x.pdfUrl) : null);
    return p?.pdfUrl || "";
  }
  return node.pdfUrl || "";
}

export const mkProduct = () => ({
  id: `prod-${Date.now()}-${Math.floor(Math.random() * 99999)}`,
  title: "New Product",
  image: "",
  images: [],
  shortDesc: "",
  description: "",
  price: "",
  mrp: "",
  currency: "",
  isFree: false,
  tags: [],
  meta: [],
  pdfUrl: "",
  ctaLabel: "",
});
